// Sticky header for the Proctor shell. Trimmed version of the Super /
// Client headers: no search (proctors have no cross-entity search), just
// a dark-mode toggle + profile dropdown with sign-out. Sits at the top of
// every proctor page so the toggle/sign-out aren't buried in the sidebar.
import { useNavigate } from "react-router-dom"
import { ChevronDown, ChevronsUpDown, LogOut } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/stores/auth"
import { logout } from "@/lib/api"
import { useTheme } from "@/contexts/ThemeContext"
import { cn } from "@/lib/utils"

function initials(input: string | undefined): string {
  if (!input) return "PR"
  const parts = input.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function ProctorHeader() {
  const navigate = useNavigate()
  const { isDark, toggle } = useTheme()
  const user = useAuthStore((s) => s.user)

  async function signOut() {
    await logout()
    navigate("/proctor/login", { replace: true })
  }

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-[var(--color-border)] bg-[var(--color-bg)] px-6">
      {/* Left: empty spacer — each page renders its own title. */}
      <div className="min-w-0 flex-1" />

      <div className="flex items-center justify-end gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-[var(--color-bg-subtle)]">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
                style={{
                  background: "var(--color-accent-light)",
                  color: "var(--color-accent-hover)",
                }}
              >
                {initials(user?.fullName ?? user?.email)}
              </span>
              <span className="hidden max-w-[140px] truncate text-sm font-medium text-foreground lg:block">
                {user?.fullName ?? user?.email ?? "Proctor"}
              </span>
              <ChevronDown className="hidden h-4 w-4 text-[color:var(--color-text-muted)] lg:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex items-center gap-3 py-2">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold"
                style={{
                  background: "var(--color-accent-light)",
                  color: "var(--color-accent-hover)",
                }}
              >
                {initials(user?.fullName ?? user?.email)}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground">
                  {user?.fullName ?? "Proctor"}
                </span>
                <span className="block truncate text-xs font-normal text-[color:var(--color-text-muted)]">
                  {user?.email}
                </span>
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              // Keep the menu open after toggling so the switch animates
              // before the user dismisses it.
              onSelect={(e) => {
                e.preventDefault()
                toggle()
              }}
            >
              <span className="flex flex-1 items-center">
                <ChevronsUpDown className="mr-2 h-4 w-4" /> Dark Mode
              </span>
              <span
                className={cn(
                  "ml-2 inline-flex h-4 w-8 items-center rounded-full px-0.5 transition-colors",
                  isDark
                    ? "bg-[var(--color-accent)]"
                    : "bg-[var(--color-border-strong)]",
                )}
              >
                <span
                  className={cn(
                    "h-3 w-3 rounded-full bg-white transition-transform",
                    isDark && "translate-x-4",
                  )}
                />
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={signOut}
              className="text-[#b91c1c] focus:text-[#b91c1c]"
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
